import type { ReactNode } from "react";
import { Check, Unlink } from "lucide-react";
import type { Currency } from "@/types";
import type {
  BankReconciliationMatch,
  BankReconciliationTransaction,
} from "@/types/bankReconciliation";
import { formatCurrency } from "../cashflow/CashflowContext";
import { formatAppDate } from "../../utils/date";
import { GRID_LINE } from "./TransactionSpreadsheetRow";

type Props = {
  transaction: BankReconciliationTransaction;
  match?: BankReconciliationMatch;
  onUnmatch?: (matchId: string) => void;
};

const CELL = `border-r ${GRID_LINE} px-3 py-2 align-middle`;
const AMOUNT_CELL = "px-3 py-2 text-right align-middle tabular-nums";

export function BankTransactionStatusRow({
  transaction,
  match,
  onUnmatch,
}: Props) {
  const currency = transaction.currency as Currency;
  const isReconciled = Boolean(match);
  const amountClass =
    transaction.amount >= 0 ? "text-emerald-700" : "text-red-600";
  const journalAmountClass = match
    ? match.journalEntry.amount >= 0
      ? "text-emerald-700"
      : "text-red-600"
    : "";

  return (
    <tr className={`border-b ${GRID_LINE} text-sm hover:bg-slate-50/40`}>
      <td className={`${CELL} max-w-0`}>
        <p className="truncate font-medium text-slate-900">
          {transaction.description}
        </p>
      </td>
      <td className={`${CELL} w-28 whitespace-nowrap text-xs text-slate-500`}>
        {formatAppDate(transaction.date)}
      </td>
      <td className={`${CELL} w-28 ${AMOUNT_CELL}`}>
        <span className={`font-medium ${amountClass}`}>
          {formatCurrency(transaction.amount, currency)}
        </span>
      </td>
      <td className={`${CELL} w-28`}>
        {isReconciled ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
            <Check className="h-3.5 w-3.5" aria-hidden />
            Reconciled
          </span>
        ) : (
          <span className="text-xs font-medium text-amber-600/80">
            Unreconciled
          </span>
        )}
      </td>
      <td className={`${CELL} max-w-0`}>
        {match ? (
          <p className="truncate text-slate-900">
            {match.journalEntry.description}
          </p>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
      <td className={`${CELL} w-28 whitespace-nowrap text-xs text-slate-500`}>
        {match ? formatAppDate(match.journalEntry.date) : "—"}
      </td>
      <td className={`${CELL} w-28 ${AMOUNT_CELL}`}>
        {match ? (
          <span className={`font-medium ${journalAmountClass}`}>
            {formatCurrency(match.journalEntry.amount, currency)}
          </span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
      <td className="w-20 px-3 py-2 align-middle">
        {match && onUnmatch ? (
          <button
            type="button"
            onClick={() => onUnmatch(match.id)}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            <Unlink className="h-3.5 w-3.5" aria-hidden />
            Unmatch
          </button>
        ) : null}
      </td>
    </tr>
  );
}

export function BankTransactionStatusTable({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <table className={`w-full border-collapse border ${GRID_LINE} text-sm`}>
      <thead>
        <tr
          className={`border-b ${GRID_LINE} bg-slate-50/50 text-xs font-medium text-slate-500`}
        >
          <th className={`${CELL} text-left font-medium`}>Description</th>
          <th className={`${CELL} w-28 text-left font-medium`}>Date</th>
          <th className={`${CELL} w-28 text-right font-medium`}>Amount</th>
          <th className={`${CELL} w-28 text-left font-medium`}>Status</th>
          <th className={`${CELL} text-left font-medium`}>Journal entry</th>
          <th className={`${CELL} w-28 text-left font-medium`}>J. date</th>
          <th className={`${CELL} w-28 text-right font-medium`}>J. amount</th>
          <th className="w-20 px-3 py-2" />
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}
